// CLEAN REIMPLEMENTATION
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { RoundButton } from '../../components/roundButton/roundButton';
import { JobIndexList } from '../../components/jobIndexList/JobIndexList';
import jobs from '../../redux/modules/jobs';
import { apiUrls } from '../../constants/apiurls';
import { baseUrl } from '../../constants/baseUrl';
import styles from '@components/jobIndexCard/JobForm.module.scss';
import { MdAdd as AddIcon } from 'react-icons/md';
import { useNavigate } from 'react-router';
import { routesPath } from '../../constants/routesPath';
import { Spinner } from '../../components/loader/loader';
import toast from 'react-hot-toast';
import { Page } from 'components/page/page';
import { EmptyCard } from '../../components/emptyCard/emptyCard';
import { Button } from '../../components/button/button';
import { fetchAutoCompleteData } from '../../services/autoCompleteService';
import { Header } from '../../components/header/header';
import { NavItem } from '../../components/nav/navItem';
import { Labeled } from '../../components/labeled/labeled';
import { authHeader } from '../../constants/authenticationHeader';
import { syncJobWorkspaceCache } from '../../services/jobWorkspaceCacheService';

export const JobsContainer = () => {
  const [showArchived, setShowArchived] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);
  const [deletedJobIds, setDeletedJobIds] = useState(() => {
    syncJobWorkspaceCache();
    try {
      return JSON.parse(localStorage.getItem('deletedJobIds') || '[]');
    } catch (e) {
      return [];
    }
  });
  const [colors, setColors] = useState([]);
  const navigate = useNavigate();

  const {
    data: jobsData,
    isFetching: isJobsLoading,
    error: jobsError,
    refetch: refetchJobs,
  } = jobs
    .list()
    .useJoblistQuery(
      { params: { page: '1', limit: '100', include_hidden: 1, include_drafts: 1 }, extendedPath: apiUrls.getJobsListing() },
      { refetchOnMountOrArgChange: true },
    );

  // Calculate job counts for tabs
  const getJobCounts = () => {
    if (!jobsData?.data?.jobs) return { active: 0, draft: 0, archived: 0 };

    let activeCount = 0;
    let draftCount = 0;
    let archivedCount = 0;

    // Get recently drafted and archived jobs from localStorage for reference
    let recentlyDraftedIds = [];
    let recentlyArchivedIds = [];
    try {
      const storedDrafted = JSON.parse(localStorage.getItem('recentlyDraftedJobs') || '[]');
      recentlyDraftedIds = storedDrafted.map(job => String(job.id));

      const storedArchived = JSON.parse(localStorage.getItem('recentlyArchivedJobs') || '[]');
      recentlyArchivedIds = storedArchived.map(job => String(job.id));
    } catch (e) {
      // Silently fail
    }

    jobsData.data.jobs.forEach(job => {
      const isDeleted = job.deleted || job.is_deleted || deletedJobIds.includes(job.id);
      if (isDeleted) return;

      const jobIdStr = String(job.id);

      const isArchived =
        job.archived === 1 ||
        job.archived === "1" ||
        job.archived === true ||
        job.status === "20" ||
        job.status === 20 ||
        job.expired === 1 ||
        job.expired === "1" ||
        job.status === "archived" ||
        recentlyArchivedIds.includes(jobIdStr);

      // A job is draft if it has BOTH hidden === 1 AND job_status === 0, or is in recently drafted list
      const isDraft = ((job.hidden === 1 || job.hidden === "1") && (job.job_status === 0 || job.job_status === "0")) || recentlyDraftedIds.includes(jobIdStr);

      if (isArchived) {
        archivedCount++;
      } else if (isDraft) {
        draftCount++;
      } else {
        activeCount++;
      }
    });

    // Also add recently drafted jobs from localStorage if not already in API data
    try {
      const storedDrafted = JSON.parse(localStorage.getItem('recentlyDraftedJobs') || '[]');
      storedDrafted.forEach(draftJob => {
        // Check if this job exists in API data by ID (handle both string and number IDs)
        const jobIdStr = String(draftJob.id);
        const existsInApiData = jobsData.data.jobs.some(job => String(job.id) === jobIdStr);
        if (!existsInApiData && !deletedJobIds.includes(draftJob.id) && !deletedJobIds.includes(jobIdStr)) {
          draftCount++;
        }
      });
    } catch (e) {
      // Silently fail if localStorage has issues
    }

    // Also add recently archived jobs from localStorage if not already in API data
    try {
      const storedArchived = JSON.parse(localStorage.getItem('recentlyArchivedJobs') || '[]');
      storedArchived.forEach(archivedJob => {
        // Check if this job exists in API data by ID (handle both string and number IDs)
        const jobIdStr = String(archivedJob.id);
        const existsInApiData = jobsData.data.jobs.some(job => String(job.id) === jobIdStr);
        if (!existsInApiData && !deletedJobIds.includes(archivedJob.id) && !deletedJobIds.includes(jobIdStr)) {
          archivedCount++;
        }
      });
    } catch (e) {
      // Silently fail if localStorage has issues
    }

    return { active: activeCount, draft: draftCount, archived: archivedCount };
  };

  const jobCounts = getJobCounts();

  // Fetch colors for job indicator badges
  useEffect(() => {
    const getColors = async () => {
      try {
        const colrs = await fetchAutoCompleteData('', apiUrls.getColors());
        setColors(colrs);
      } catch (err) {
        console.error('Failed to fetch colors:', err);
      }
    };
    getColors();
  }, []);

  useEffect(() => {
    if (jobsError) toast.error('Failed To Get Jobs!');
  }, [jobsError]);

  useEffect(() => {
  }, [jobsData]);

  useEffect(() => {
    const handleSwitchToArchived = () => {
      setShowArchived(true);
      setShowDrafts(false);
    };

    const handleSwitchToActive = () => {
      setShowArchived(false);
      setShowDrafts(false);
    };

    const handleSwitchToDraft = () => {
      setShowDrafts(true);
      setShowArchived(false);
      // Refetch jobs to ensure we have the latest data with draft jobs
      refetchJobs();
    };

    window.addEventListener('switchToArchivedView', handleSwitchToArchived);
    window.addEventListener('switchToActiveView', handleSwitchToActive);
    window.addEventListener('switchToDraftView', handleSwitchToDraft);

    return () => {
      window.removeEventListener('switchToArchivedView', handleSwitchToArchived);
      window.removeEventListener('switchToActiveView', handleSwitchToActive);
      window.removeEventListener('switchToDraftView', handleSwitchToDraft);
    };
  }, []);

  const handleDeleteJob = async (jobId, jobTitle, isDraft = false) => {
    if (
      window.confirm(
        `Are you sure you want to delete the job "${jobTitle || 'Untitled Job'}"? This action cannot be undone.`
      )
    ) {
      try {
        console.log('Attempting to delete job ID:', jobId, 'isDraft:', isDraft);

        // Immediately hide from UI
        setDeletedJobIds(prev => [...prev, jobId]);

        // For draft jobs or any job, use the delete endpoint
        const deleteResponse = await fetch(`${baseUrl}/rest/jobs/delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeader().common,
          },
          body: JSON.stringify({
            id: jobId,
          }),
        });

        const deleteResult = await deleteResponse.json();
        console.log('Delete response:', deleteResult);

        if (deleteResponse.ok && deleteResult.success) {
          toast.success('Job deleted successfully!');

          // Remove from drafted jobs localStorage if it's a draft
          if (isDraft) {
            try {
              const draftedJobs = JSON.parse(localStorage.getItem('recentlyDraftedJobs') || '[]');
              const updatedDrafts = draftedJobs.filter(draftJob =>
                String(draftJob.id) !== String(jobId)
              );
              localStorage.setItem('recentlyDraftedJobs', JSON.stringify(updatedDrafts));

              // Dispatch event to update the drafted jobs list
              const event = new CustomEvent('draftedJobsUpdated', {
                detail: { draftedJobs: updatedDrafts }
              });
              window.dispatchEvent(event);
            } catch (e) {
              console.error('Failed to remove from drafted jobs:', e);
            }
          }

          // Store deleted ID in localStorage to persist across page reloads
          try {
            const existingDeleted = JSON.parse(localStorage.getItem('deletedJobIds') || '[]');
            localStorage.setItem('deletedJobIds', JSON.stringify([...existingDeleted, jobId]));
          } catch (e) {
            console.error('localStorage save error:', e);
          }

          // Reload page to refresh the list
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } else {
          toast.error(deleteResult?.message || 'Failed to delete job');
        }

      } catch (error) {
        console.error('Delete job error:', error);
        toast.error('Failed to delete job');
        toast.error(`Failed to delete job: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const jobsForCounting = (jobsData?.data?.jobs || [])
    .filter((job) => !job.deleted && !job.is_deleted)
    .filter((job) => {
      const isArchived =
        job.archived === 1 ||
        job.archived === '1' ||
        job.archived === true ||
        job.status === '20' ||
        job.status === 20 ||
        job.expired === 1 ||
        job.expired === '1' ||
        job.status === 'archived' ||
        job.job?.archived === 1 ||
        job.job?.archived === '1' ||
        job.job?.archived === true ||
        job.job?.status === 'archived' ||
        job.job?.status === '20' ||
        job.job?.expired === 1;
      return showArchived ? isArchived : !isArchived;
    });

  const clientJobCounts = jobsForCounting.reduce((acc, job) => {
    const cid = job.client_id || job.client?.id;
    if (!cid) return acc;
    acc[cid] = (acc[cid] || 0) + 1;
    return acc;
  }, {});

  const countBoxStyle = {
    background: '#f3f3f3',
    color: '#666',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: 8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 18,
    lineHeight: '18px',
    textAlign: 'center',
    borderRadius: 0,
    boxShadow: 'none',
    border: 'none',
    padding: 0,
    boxSizing: 'border-box'
  };

  return (
    <div className={classNames()}>
      {isJobsLoading ? (
        <div className="text-center">
          <Spinner height="100" width="100" />
        </div>
      ) : (
        <>
          {/* Header with Tabs */}
          <Header title={showDrafts ? 'Draft Job Posts' : showArchived ? 'Archived Job Posts' : 'Active Job Posts'}>
            <NavItem
              active={!showDrafts && !showArchived}
              onClick={() => {
                setShowArchived(false);
                setShowDrafts(false);
              }}
            >
              <Labeled counted={jobCounts.active}>Active Jobs</Labeled>
            </NavItem>
            <NavItem
              active={showDrafts}
              onClick={() => {
                setShowDrafts(true);
                setShowArchived(false);
                refetchJobs();
              }}
            >
              <Labeled counted={jobCounts.draft}>Draft Jobs</Labeled>
            </NavItem>
            <NavItem
              active={showArchived}
              onClick={() => {
                setShowArchived(true);
                setShowDrafts(false);
              }}
            >
              <Labeled counted={jobCounts.archived}>Archived Jobs</Labeled>
            </NavItem>
          </Header>

          <Page isNotSubmenu style={{ marginTop: '10px' }}>
            {!showArchived && !showDrafts && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '0px', marginBottom: '10px', marginTop: '-10px' }}>
                <RoundButton
                  theme="blue"
                  onClick={() => navigate(routesPath.jobsFormPage, { replace: false })}
                >
                  <AddIcon />
                </RoundButton>
              </div>
            )}
            <div className={classNames(styles.jobsWrapper)}>
              {!jobsData?.data?.jobs?.length ? (
                <EmptyCard
                  isCard
                  title="Let's get started. Add your first Job."
                  description="Who's the company that you're recruiting for? We first need to create a job and then we can start by adding projects we need to recruit for."
                >
                  <Button
                    submit
                    onClick={() => navigate(routesPath.jobsFormPage, { replace: false })}
                    inline
                  >
                    New Job
                  </Button>
                </EmptyCard>
              ) : (
                <JobIndexList
                  jobs={jobsData?.data?.jobs?.filter((job) => {
                    // Filter out deleted jobs and manually deleted jobs
                    const isDeleted = job.deleted || job.is_deleted || deletedJobIds.includes(job.id);
                    return !isDeleted;
                  }) || []}
                  colors={colors}
                  showArchived={showArchived}
                  showDrafts={showDrafts}
                  deleteJob={handleDeleteJob}
                />
              )}
            </div>
          </Page>
        </>
      )}
    </div>
  );
};
